import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { extrahiereText, parseModulwerte } from '../utils/gutachtenPdf';

// ==========================================================================
// PflegegradPruefer — klientenbezogener Tab im Berater-Dashboard.
//
// Eingabe der Summenwerte der sechs NBA-Module -> Live-Berechnung von
// Pflegegrad, Schwellenwert-Analyse und Vergleich heute<->2027 ueber
// POST /api/begutachtung/berechnen. Speicherung pro Kunde.
// ==========================================================================

const MODULE = [
  { id: 1, name: 'Mobilität', max: 15 },
  { id: 2, name: 'Kognitive & kommunikative Fähigkeiten', max: 33 },
  { id: 3, name: 'Verhaltensweisen & psychische Problemlagen', max: 65 },
  { id: 4, name: 'Selbstversorgung', max: 54 },
  { id: 5, name: 'Krankheits-/therapiebedingte Anforderungen', max: 15 },
  { id: 6, name: 'Alltagsleben & soziale Kontakte', max: 18 },
];

const PG_FARBE = {
  0: 'bg-slate-200 text-slate-700',
  1: 'bg-emerald-100 text-emerald-800',
  2: 'bg-amber-100 text-amber-800',
  3: 'bg-orange-100 text-orange-800',
  4: 'bg-rose-100 text-rose-800',
  5: 'bg-red-200 text-red-900',
};

function PgBadge({ pg, gross }) {
  const label = pg === 0 ? 'kein PG' : `PG ${pg}`;
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-bold ${PG_FARBE[pg] || PG_FARBE[0]} ${gross ? 'px-5 py-2 text-2xl' : 'px-3 py-1 text-sm'}`}>
      {label}
    </span>
  );
}

const LEERE_SUMMEN = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

export default function PflegegradPruefer({ kundeId, kunde }) {
  const [summen, setSummen] = useState({ ...LEERE_SUMMEN });
  const [besondere, setBesondere] = useState(false);
  const [bescheidPg, setBescheidPg] = useState('');   // '' | '1'..'5'
  const [bezeichnung, setBezeichnung] = useState('');
  const [notiz, setNotiz] = useState('');

  const [ergebnis, setErgebnis] = useState(null);
  const [gespeicherte, setGespeicherte] = useState([]);
  const [speichern, setSpeichern] = useState(false);
  const [fehler, setFehler] = useState(null);

  // --- PDF-Auslesen (Etappe 4) ---------------------------------------------
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfFehler, setPdfFehler] = useState(null);
  const [pdfDateiname, setPdfDateiname] = useState('');
  const [kandidaten, setKandidaten] = useState(null);   // null | [{modul,wert,gefunden,kontext}]
  const [rohtext, setRohtext] = useState('');
  const [zeigeRohtext, setZeigeRohtext] = useState(false);
  const [scanHinweis, setScanHinweis] = useState(false);
  const fileInputRef = useRef(null);

  const debounceRef = useRef(null);

  // --- Gespeicherte Begutachtungen laden -----------------------------------
  const ladeListe = useCallback(async () => {
    if (!kundeId) return;
    try {
      const liste = await api.get(`/api/kunden/${kundeId}/begutachtungen`);
      setGespeicherte(Array.isArray(liste) ? liste : []);
    } catch (e) {
      console.warn('Begutachtungen laden fehlgeschlagen', e);
    }
  }, [kundeId]);

  useEffect(() => { ladeListe(); }, [ladeListe]);

  // --- Live-Berechnung (debounced) -----------------------------------------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const body = {
          modul_1: summen[1], modul_2: summen[2], modul_3: summen[3],
          modul_4: summen[4], modul_5: summen[5], modul_6: summen[6],
          besondere_bedarfskonstellation: besondere,
          bescheid_pflegegrad: bescheidPg === '' ? null : Number(bescheidPg),
        };
        const erg = await api.post('/api/begutachtung/berechnen', body);
        setErgebnis(erg);
        setFehler(null);
      } catch (e) {
        console.warn('Berechnung fehlgeschlagen', e);
        setFehler('Berechnung gerade nicht möglich.');
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [summen, besondere, bescheidPg]);

  const setModul = (id, wert) => {
    const max = MODULE.find((m) => m.id === id).max;
    let n = parseInt(wert, 10);
    if (Number.isNaN(n)) n = 0;
    n = Math.max(0, Math.min(n, max));
    setSummen((s) => ({ ...s, [id]: n }));
  };

  const zuruecksetzen = () => {
    setSummen({ ...LEERE_SUMMEN });
    setBesondere(false);
    setBescheidPg('');
    setBezeichnung('');
    setNotiz('');
  };

  const handleSpeichern = async () => {
    if (!kundeId) return;
    setSpeichern(true);
    setFehler(null);
    try {
      const body = {
        bezeichnung: bezeichnung || null,
        notiz: notiz || null,
        modul_1: summen[1], modul_2: summen[2], modul_3: summen[3],
        modul_4: summen[4], modul_5: summen[5], modul_6: summen[6],
        besondere_bedarfskonstellation: besondere,
        bescheid_pflegegrad: bescheidPg === '' ? null : Number(bescheidPg),
      };
      await api.post(`/api/kunden/${kundeId}/begutachtungen`, body);
      await ladeListe();
    } catch (e) {
      console.warn('Speichern fehlgeschlagen', e);
      setFehler('Speichern fehlgeschlagen.');
    } finally {
      setSpeichern(false);
    }
  };

  const ladeGespeicherte = (b) => {
    setSummen({ 1: b.modul_1, 2: b.modul_2, 3: b.modul_3,
                4: b.modul_4, 5: b.modul_5, 6: b.modul_6 });
    setBesondere(b.besondere_bedarfskonstellation);
    setBescheidPg(b.bescheid_pflegegrad == null ? '' : String(b.bescheid_pflegegrad));
    setBezeichnung(b.bezeichnung || '');
    setNotiz(b.notiz || '');
  };

  const loescheGespeicherte = async (id) => {
    if (!window.confirm('Diese gespeicherte Begutachtung löschen?')) return;
    try {
      await api.del(`/api/begutachtungen/${id}`);
      await ladeListe();
    } catch (e) {
      console.warn('Löschen fehlgeschlagen', e);
    }
  };

  // --- PDF einlesen: Text extrahieren (lokal!) + Modul-Werte erkennen ------
  const handlePdfDatei = async (file) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setPdfFehler('Bitte eine PDF-Datei auswählen.');
      return;
    }
    setPdfBusy(true);
    setPdfFehler(null);
    setScanHinweis(false);
    setKandidaten(null);
    setPdfDateiname(file.name);
    try {
      const { text, vermutlichScan } = await extrahiereText(file);
      setRohtext(text);
      setScanHinweis(vermutlichScan);
      setKandidaten(parseModulwerte(text));
    } catch (e) {
      console.warn('PDF-Auslesen fehlgeschlagen', e);
      setPdfFehler('Das PDF konnte nicht gelesen werden. Ist es evtl. ein reiner Scan (Bild)?');
    } finally {
      setPdfBusy(false);
    }
  };

  const setKandidatWert = (modul, wert) => {
    const max = MODULE.find((m) => m.id === modul).max;
    let n = parseInt(wert, 10);
    if (Number.isNaN(n)) n = 0;
    n = Math.max(0, Math.min(n, max));
    setKandidaten((ks) => (ks || []).map((k) => k.modul === modul ? { ...k, wert: n, gefunden: true } : k));
  };

  const uebernehmeKandidaten = () => {
    if (!kandidaten) return;
    const neu = { ...LEERE_SUMMEN };
    kandidaten.forEach((k) => { if (k.wert != null) neu[k.modul] = k.wert; });
    setSummen(neu);
    // Review-Panel eingeklappt lassen, aber Zuordnung sichtbar halten.
  };

  const verwerfePdf = () => {
    setKandidaten(null);
    setPdfDateiname('');
    setRohtext('');
    setZeigeRohtext(false);
    setScanHinweis(false);
    setPdfFehler(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const aktuell = ergebnis?.aktuell;
  const regel2027 = ergebnis?.regel_2027;
  const analyse = ergebnis?.schwellenwert_analyse;
  const vergleich = ergebnis?.vergleich;
  const abgleich = ergebnis?.bescheid_abgleich;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-1 text-slate-700">
          Pflegegutachten-Prüfer
        </h3>
        <p className="text-xs text-slate-500">
          Summenwerte der sechs Module aus dem MD-/MEDICPROOF-Gutachten eintragen —
          Pflegegrad, Schwellenwert-Analyse und der Vergleich mit den ab 2027
          geplanten Regeln werden sofort berechnet.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ---------- Eingabe ---------- */}
        <div className="space-y-4">
          {/* ----- PDF-Auslesen (Etappe 4) ----- */}
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4 shadow-sm">
            <h4 className="mb-1 text-sm font-semibold text-indigo-900">Gutachten-PDF automatisch auslesen</h4>
            <p className="mb-3 text-xs text-indigo-700/80">
              PDF des MD-/MEDICPROOF-Gutachtens auswählen — die sechs Modul-Werte
              werden vorgeschlagen. Die Datei bleibt <b>lokal auf diesem Rechner</b>,
              es wird nichts hochgeladen.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => handlePdfDatei(e.target.files?.[0])}
              className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-indigo-700"
            />

            {pdfBusy && (
              <p className="mt-3 text-xs text-indigo-700">PDF wird gelesen …</p>
            )}
            {pdfFehler && (
              <p className="mt-3 rounded-lg bg-rose-100 px-3 py-2 text-xs text-rose-800">{pdfFehler}</p>
            )}
            {scanHinweis && (
              <p className="mt-3 rounded-lg bg-amber-100 px-3 py-2 text-xs text-amber-800">
                In diesem PDF wurde kaum Text gefunden — vermutlich ein eingescanntes
                Dokument (Bild). Die Werte müssen dann leider von Hand eingetragen werden.
              </p>
            )}

            {kandidaten && (
              <div className="mt-3 rounded-xl border border-indigo-200 bg-white p-3">
                <p className="mb-2 text-xs font-semibold text-slate-700">
                  Gefundene Werte aus „{pdfDateiname}" — bitte prüfen und ggf. korrigieren:
                </p>
                <div className="space-y-1.5">
                  {kandidaten.map((k) => {
                    const m = MODULE.find((mm) => mm.id === k.modul);
                    return (
                      <div key={k.modul} className="flex items-center gap-2">
                        <span className="w-6 text-center text-xs font-bold text-slate-400">{k.modul}</span>
                        <span className="flex-1 truncate text-xs text-slate-600" title={k.kontext || ''}>
                          {m.name}
                        </span>
                        <input
                          type="number" min={0} max={m.max}
                          value={k.wert == null ? '' : k.wert}
                          placeholder="?"
                          onChange={(e) => setKandidatWert(k.modul, e.target.value)}
                          className={`w-16 rounded-lg border px-2 py-1 text-right text-sm outline-none ${k.gefunden ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
                        />
                        <span className="w-10 text-xs text-slate-400">/ {m.max}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={uebernehmeKandidaten}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"
                  >
                    In die Maske übernehmen
                  </button>
                  <button
                    onClick={verwerfePdf}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Verwerfen
                  </button>
                  <button
                    onClick={() => setZeigeRohtext((v) => !v)}
                    className="ml-auto text-xs text-slate-500 underline hover:text-slate-700"
                  >
                    {zeigeRohtext ? 'Rohtext ausblenden' : 'Rohtext anzeigen'}
                  </button>
                </div>

                <p className="mt-2 text-[11px] leading-snug text-slate-400">
                  Grün = automatisch erkannt, Gelb = bitte selbst eintragen. Die Erkennung
                  ist eine Hilfe — maßgeblich bleibt das Gutachten.
                </p>

                {zeigeRohtext && (
                  <textarea
                    readOnly
                    value={rohtext}
                    rows={8}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] font-mono text-slate-600"
                  />
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-sm font-semibold text-slate-700">Summenwerte je Modul</h4>
            <div className="space-y-2">
              {MODULE.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <span className="w-7 shrink-0 text-center text-xs font-bold text-slate-400">{m.id}</span>
                  <span className="flex-1 text-sm text-slate-700">{m.name}</span>
                  <input
                    type="number"
                    min={0}
                    max={m.max}
                    value={summen[m.id]}
                    onChange={(e) => setModul(m.id, e.target.value)}
                    className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-right text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                  <span className="w-12 shrink-0 text-xs text-slate-400">/ {m.max}</span>
                </div>
              ))}
            </div>

            <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={besondere}
                     onChange={(e) => setBesondere(e.target.checked)}
                     className="h-4 w-4 rounded border-slate-400" />
              Besondere Bedarfskonstellation (→ Pflegegrad 5)
            </label>

            <div className="mt-3 flex items-center gap-2">
              <label className="text-sm text-slate-700">Pflegegrad lt. Bescheid:</label>
              <select value={bescheidPg} onChange={(e) => setBescheidPg(e.target.value)}
                      className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option value="">—</option>
                {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>PG {p}</option>)}
              </select>
              <span className="text-xs text-slate-400">(optional, zum Abgleich)</span>
            </div>
          </div>

          {/* Speichern */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <input
              type="text" value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)}
              placeholder="Bezeichnung (z. B. „Erstgutachten 03/2026“)"
              className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <textarea
              value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2}
              placeholder="Notiz (optional)"
              className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={handleSpeichern} disabled={speichern || !kundeId}
                      className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
                {speichern ? 'Speichern …' : 'Begutachtung speichern'}
              </button>
              <button onClick={zuruecksetzen}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Zurücksetzen
              </button>
            </div>
            {fehler && <p className="mt-2 text-sm text-rose-600">{fehler}</p>}
          </div>
        </div>

        {/* ---------- Ergebnis ---------- */}
        <div className="space-y-4">
          {aktuell && (
            <>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-400">{aktuell.regelsatz_label}</p>
                    <p className="mt-1 text-sm text-slate-600">{aktuell.gesamtpunkte} Gesamtpunkte</p>
                  </div>
                  <PgBadge pg={aktuell.pflegegrad} gross />
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {aktuell.module.map((m) => (
                      <tr key={m.modul} className="border-t border-slate-100">
                        <td className="py-1 pr-2 text-slate-400">{m.modul}</td>
                        <td className="py-1 pr-2 text-slate-700">{m.name}</td>
                        <td className="py-1 pr-2 text-right text-slate-500">{m.summenwert}</td>
                        <td className="py-1 text-right font-semibold text-slate-800">
                          {m.gewichtete_punkte}
                          {(m.modul === 2 || m.modul === 3) && !m.zaehlt_fuer_gesamt &&
                            <span className="ml-1 text-xs font-normal text-slate-400">(zählt nicht)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Schwellenwert-Analyse */}
              {analyse && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h4 className="mb-2 text-sm font-semibold text-slate-700">Schwellenwert-Analyse</h4>
                  {analyse.naechster_pflegegrad ? (
                    <p className="mb-2 text-sm text-slate-600">
                      Nächster Pflegegrad <b>PG {analyse.naechster_pflegegrad.pflegegrad}</b> ab{' '}
                      {analyse.naechster_pflegegrad.ab_punkten} Punkten — Abstand nur{' '}
                      <b>{analyse.naechster_pflegegrad.punkte_abstand}</b>.
                    </p>
                  ) : (
                    <p className="mb-2 text-sm text-slate-500">Höchster Pflegegrad bereits erreicht.</p>
                  )}
                  {analyse.hebel.filter((h) => h.kippt_pflegegrad).length > 0 ? (
                    <>
                      <p className="text-xs font-semibold text-slate-500">
                        Einzelne Höherstufungen, die den Pflegegrad kippen würden:
                      </p>
                      <ul className="mt-1 space-y-1">
                        {analyse.hebel.filter((h) => h.kippt_pflegegrad).map((h) => (
                          <li key={h.modul} className="text-sm text-slate-700">
                            • <b>Modul {h.modul}</b> ({h.name}): +{h.fehlende_summenpunkte} Summenpunkte
                            → Gesamt {h.gesamt_danach} → <b>PG {h.pflegegrad_danach}</b>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Keine einzelne Modul-Höherstufung kippt aktuell den Pflegegrad.
                    </p>
                  )}
                </div>
              )}

              {/* Vergleich 2027 */}
              {regel2027 && vergleich && (
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5 shadow-sm">
                  <h4 className="mb-3 text-sm font-semibold text-indigo-900">Vergleich: heute ↔ 2027</h4>
                  <div className="flex items-center justify-around text-center">
                    <div>
                      <p className="text-xs text-slate-500">Aktuell (BRi 2024)</p>
                      <div className="mt-1"><PgBadge pg={aktuell.pflegegrad} /></div>
                      <p className="mt-1 text-xs text-slate-400">{aktuell.gesamtpunkte} P.</p>
                    </div>
                    <div className="text-2xl text-slate-300">→</div>
                    <div>
                      <p className="text-xs text-slate-500">Geplant ab 2027</p>
                      <div className="mt-1"><PgBadge pg={regel2027.pflegegrad} /></div>
                      <p className="mt-1 text-xs text-slate-400">{regel2027.gesamtpunkte} P.</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-indigo-900">{vergleich.hinweis}</p>
                </div>
              )}

              {/* Bescheid-Abgleich */}
              {abgleich && (
                <div className={`rounded-2xl border p-4 shadow-sm ${abgleich.abweichung > 0 ? 'border-emerald-300 bg-emerald-50' : abgleich.abweichung < 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                  <p className="text-sm font-semibold text-slate-700">
                    Bescheid PG {abgleich.bescheid_pflegegrad} · eigene Nachrechnung PG {abgleich.berechneter_pflegegrad}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{abgleich.hinweis}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Gespeicherte Begutachtungen */}
      {gespeicherte.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="mb-2 text-sm font-semibold text-slate-700">
            Gespeicherte Begutachtungen ({gespeicherte.length})
          </h4>
          <ul className="divide-y divide-slate-100">
            {gespeicherte.map((b) => (
              <li key={b.id} className="flex items-center gap-3 py-2">
                <PgBadge pg={b.ergebnis?.aktuell?.pflegegrad ?? 0} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {b.bezeichnung || 'Ohne Bezeichnung'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {b.created_at ? new Date(b.created_at).toLocaleDateString('de-DE') : ''}
                    {b.ergebnis?.regel_2027 && ` · 2027: PG ${b.ergebnis.regel_2027.pflegegrad}`}
                  </p>
                </div>
                <button onClick={() => ladeGespeicherte(b)}
                        className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  Laden
                </button>
                <button onClick={() => loescheGespeicherte(b.id)}
                        className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50">
                  Löschen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-slate-400">
        Fachliche Arbeitshilfe für die Pflegeberatung — ersetzt keine Rechtsberatung
        und keine eigenverantwortliche fachliche Prüfung. Die 2027er-Werte beruhen auf
        der geplanten Reform (PNUG) und stehen unter Vorbehalt der finalen Gesetzgebung.
      </p>
    </div>
  );
}
